import { z } from 'zod'

/**
 * Item de dirección dentro de un form de **edición** (no de creación). A
 * diferencia de `addressSchema`, lleva `id` (presente = existente; ausente =
 * nueva) y `_deleted` (marca para borrar: se envía como `{ id, delete: true }`).
 *
 * Se exportan dos variantes:
 * - `addressEditItemBaseSchema`: objeto plano sin validación de obligatoriedad.
 *   Útil cuando el editor está deshabilitado / no se envía (p. ej. organización
 *   mientras el backend no acepta `addresses`) y no debe bloquear el guardado.
 * - `addressEditItemSchema`: el anterior + `superRefine` con los campos
 *   obligatorios (para negocio, donde sí se editan y envían).
 */
export const addressEditItemBaseSchema = z.object({
  id: z.number().int().positive().optional(),
  _deleted: z.boolean().optional(),
  street1: z.string().trim().max(200),
  city: z.string().trim().max(120),
  countryId: z.number().int(),
  provinceId: z.number().int(),
  municipalityId: z.number().int(),
  // Coordenadas geográficas (opcionales). Se guardan como string (así las
  // modela Zeus); vacío = sin ubicación fijada.
  coordinateLatitude: z.string().trim().optional(),
  coordinateLongitude: z.string().trim().optional(),
  isMain: z.boolean(),
})

export const addressEditItemSchema = addressEditItemBaseSchema.superRefine((val, ctx) => {
  if (val._deleted) return
  if (val.street1.trim().length < 1)
    ctx.addIssue({ code: 'custom', path: ['street1'], message: 'La calle es obligatoria' })
  if (val.city.trim().length < 1)
    ctx.addIssue({ code: 'custom', path: ['city'], message: 'La ciudad es obligatoria' })
  if (val.countryId <= 0)
    ctx.addIssue({ code: 'custom', path: ['countryId'], message: 'Selecciona un país' })
  if (val.provinceId <= 0)
    ctx.addIssue({ code: 'custom', path: ['provinceId'], message: 'Selecciona una provincia' })
  if (val.municipalityId <= 0)
    ctx.addIssue({ code: 'custom', path: ['municipalityId'], message: 'Selecciona un municipio' })
})

export type AddressEditItem = z.infer<typeof addressEditItemBaseSchema>

/** Fila vacía para `useFieldArray.append` al agregar una dirección nueva. */
export const emptyAddressEditItem = (): AddressEditItem => ({
  _deleted: false,
  street1: '',
  city: '',
  countryId: 0,
  provinceId: 0,
  municipalityId: 0,
  coordinateLatitude: '',
  coordinateLongitude: '',
  isMain: false,
})
